export interface PageInfo {
  name: string;
  category: string;
  studioId?: string;
  metadata?: Record<string, any>;
}

export class StudentPageMapper {
  private static exactRoutes: Record<string, PageInfo> = {
    '/': { name: 'Home', category: 'Navigation' },
    '/search': { name: 'Search', category: 'Discovery' },

    '/login': { name: 'Login', category: 'Authentication' },
    '/clientLogin': { name: 'Student Login', category: 'Authentication' },
    '/signUp': { name: 'Sign Up', category: 'Authentication' },
    '/forgetPassword': { name: 'Forget Password', category: 'Authentication' },
    '/resetPassword': { name: 'Reset Password', category: 'Authentication' },

    '/myAccount': { name: 'My Account', category: 'Account Management' },
    '/changePassword': { name: 'Change Password', category: 'Account Management' },
    '/changeEmail': { name: 'Change Email', category: 'Account Management' },
    '/me': { name: 'My QR Code', category: 'Account Management' },

    '/myClasses': { name: 'My Classes', category: 'Classes' },

    '/privacyPolicy': { name: 'Privacy Policy', category: 'Legal' },
    '/termsAndConditions': { name: 'Terms and Conditions', category: 'Legal' },
  };

  private static dynamicRoutes: Array<{
    pattern: RegExp;
    name: string;
    category: string;
    getMetadata?: (match: RegExpMatchArray) => Record<string, any>;
  }> = [
    {
      pattern: /^\/studio\/([^\/]+)$/,
      name: 'Studio Page',
      category: 'Studio Discovery',
      getMetadata: (match) => ({ studioSlug: match[1] }),
    },
    {
      pattern: /^\/studio\/([^\/]+)\/booking\/([^\/]+)$/,
      name: 'Class Booking',
      category: 'Booking',
      getMetadata: (match) => ({
        studioSlug: match[1],
        courseId: match[2],
        conversion_funnel: 'booking',
      }),
    },
    {
      pattern: /^\/studio\/([^\/]+)\/course\/([^\/]+)$/,
      name: 'Course Page',
      category: 'Studio Discovery',
      getMetadata: (match) => ({ studioSlug: match[1], courseId: match[2] }),
    },
    {
      pattern: /^\/class\/([^\/]+)$/,
      name: 'Class Info',
      category: 'Classes',
      getMetadata: (match) => ({ classId: match[1] }),
    },

    {
      pattern: /^\/myClasses\/([^\/]+)$/,
      name: 'Studio Classes',
      category: 'Classes',
      getMetadata: (match) => ({ studioId: match[1] }),
    },
    {
      pattern: /^\/clientAttendance\/([^\/]+)$/,
      name: 'Client Attendance',
      category: 'Classes',
      getMetadata: (match) => ({ attendanceId: match[1] }),
    },

    {
      pattern: /^\/search\/([^\/]+)$/,
      name: 'Search Results',
      category: 'Discovery',
      getMetadata: (match) => ({ query: match[1] }),
    },
    {
      pattern: /^\/search\/category\/([^\/]+)$/,
      name: 'Category Search',
      category: 'Discovery',
      getMetadata: (match) => ({ category: match[1] }),
    },
    {
      pattern: /^\/booking-rate-plan\/([^\/]+)\/([^\/]+)$/,
      name: 'Booking RatePlan Page',
      category: 'Booking',
      getMetadata: (match) => {
        const urlParams = new URLSearchParams(window.location.search);
        const orderId = urlParams.get('orderId');
        const studioClientId = urlParams.get('studioClientId');
        const paymentSource = urlParams.get('paymentSource');

        let pageState: string;
        let conversionFunnel: string;
        let pageName: string;

        if (orderId) {
          pageState = 'payment_success';
          conversionFunnel = 'rate_plan_payment_success';
          pageName = 'Rate Plan Payment Success';
        } else if (studioClientId && paymentSource) {
          pageState = 'checkout';
          conversionFunnel = 'rate_plan_checkout';
          pageName = 'Rate Plan Checkout';
        } else {
          pageState = 'view';
          conversionFunnel = 'rate_plan_view';
          pageName = 'Rate Plan View';
        }

        return {
          studioId: match[1],
          ratePlanId: match[2],
          studioClientId,
          paymentSource,
          orderId,
          page_state: pageState,
          conversion_funnel: conversionFunnel,
          page_name_override: pageName,
        };
      },
    },

    {
      pattern: /^\/stripe\/get-link\/([^\/]+)$/,
      name: 'Account Top-up',
      category: 'Payment & Billing',
      getMetadata: (match) => {
        const urlParams = new URLSearchParams(window.location.search);
        const amount = urlParams.get('amount');
        const clientId = urlParams.get('clientId');

        return {
          studioId: match[1],
          clientId,
          topup_amount: amount ? parseInt(amount) : null,
          topup_amount_formatted: amount ? (parseInt(amount) / 100).toFixed(2) : null,
          payment_provider: 'stripe',
          conversion_funnel: 'account_topup',
          page_state: 'topup_checkout',
        };
      },
    },
    {
      pattern: /^\/liqPay\/([^\/]+)$/,
      name: 'Account Top-up',
      category: 'Payment & Billing',
      getMetadata: (match) => {
        const urlParams = new URLSearchParams(window.location.search);
        const amount = urlParams.get('amount');
        const clientId = urlParams.get('clientId');

        return {
          studioId: match[1],
          clientId,
          topup_amount: amount ? parseInt(amount) : null,
          topup_amount_formatted: amount ? (parseInt(amount) / 100).toFixed(2) : null,
          payment_provider: 'liqpay',
          conversion_funnel: 'account_topup',
          page_state: 'topup_checkout',
        };
      },
    },
    {
      pattern: /^\/wayForPay\/([^\/]+)$/,
      name: 'Account Top-up',
      category: 'Payment & Billing',
      getMetadata: (match) => {
        const urlParams = new URLSearchParams(window.location.search);
        const amount = urlParams.get('amount');
        const clientId = urlParams.get('clientId');

        return {
          studioId: match[1],
          clientId,
          topup_amount: amount ? parseInt(amount) : null,
          topup_amount_formatted: amount ? (parseInt(amount) / 100).toFixed(2) : null,
          payment_provider: 'wayforpay',
          conversion_funnel: 'account_topup',
          page_state: 'topup_checkout',
        };
      },
    },
  ];

  public static getPageInfo(pathname: string): PageInfo {
    if (this.exactRoutes[pathname]) {
      return this.exactRoutes[pathname];
    }

    for (const route of this.dynamicRoutes) {
      const match = pathname.match(route.pattern);
      if (match) {
        const metadata = route.getMetadata ? route.getMetadata(match) : {};
        return {
          name: route.name,
          category: route.category,
          metadata,
        };
      }
    }

    return {
      name: pathname.replace(/^\//, '').replace(/\//g, ' ') || 'Home',
      category: 'Other',
    };
  }
}
